import { Modal, Button, Space, Typography, theme, Card } from 'antd';
import { FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import httpClient from '../services/http-client';
import { formModalWidth } from '@/components/ui/formModalStyles';

import useAppNotification from '@/hooks/useAppNotification';
const { Text } = Typography;
const { useToken } = theme;

export interface FileItem {
  storageName: string;
  originalName: string;
  downloadUrl: string;
}

interface FileListModalProps {
  open: boolean;
  onClose: () => void;
  files: FileItem[];
  title: string;
}

export function FileListModal({ open, onClose, files, title }: FileListModalProps) {
  const { token } = useToken();
  const { t } = useTranslation('job-module');
  const { showSuccessMessage, showErrorMessage } = useAppNotification();

  const handleDownload = async (file: FileItem) => {
    try {
      const response = await httpClient.get('/__download', {
        params: { url: file.downloadUrl },
        responseType: 'blob',
      });

      // Create a blob from the response
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccessMessage(t('files.downloadSuccess', { name: file.originalName }));
    } catch (error) {
      console.error('Error downloading file:', error);
      showErrorMessage(t('files.downloadError'));
    }
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onClose}
      footer={null}
      width={formModalWidth(600)}
    >
      {files.length === 0 ? (
        <Text type="secondary">{t('files.noFiles')}</Text>
      ) : (
        <Space orientation="vertical" size="middle" style={{ width: '100%' }}>
          {files.map((file, index) => (
            <Card
              key={index}
              size="small"
              styles={{
                body: {
                  padding: token.paddingSM
                }
              }}
            >
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Space size="middle">
                  <FileTextOutlined 
                    style={{ 
                      fontSize: token.fontSizeLG, 
                      color: token.colorPrimary 
                    }} 
                  />
                  <Space orientation="vertical" size={0}>
                    <Text strong>{file.originalName}</Text>
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      {file.storageName}
                    </Text>
                  </Space>
                </Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={() => handleDownload(file)}
                >
                  {t('files.download')}
                </Button>
              </Space>
            </Card>
          ))}
        </Space>
      )}
    </Modal>
  );
}
